import { useState, useEffect } from 'react';
import { UserCircle, MessageSquare, Calendar } from 'lucide-react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  userData: any[];
  assistant: any;
  onDateRangeChange?: (startDate: string, endDate: string) => void;
  fromDate?: string;
  toDate?: string;
}

export default function TopUserActivity({ 
  userData, 
  assistant, 
  onDateRangeChange, 
  fromDate,
  toDate 
}: Props) {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [showAll, setShowAll] = useState<boolean>(false);
  
  // Helper function to validate date strings
  const isValidDate = (dateString: string): boolean => {
    if (!dateString || dateString.trim() === '') return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayString = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Helper function to get a default start date (7 days ago)
  const getDefaultStartDate = (): string => {
    if (fromDate && isValidDate(fromDate)) return fromDate;
    const today = new Date();
    today.setDate(today.getDate() - 6);
    return today.toISOString().split('T')[0];
  };

  // Helper function to get a default end date
  const getDefaultEndDate = (): string => {
    if (toDate && isValidDate(toDate)) return toDate;
    return getTodayString();
  };

  // Calculate end date (max 7 days from start date)
  function calculateEndDate(startDateStr: string): string {
    if (!startDateStr || !isValidDate(startDateStr)) {
      return getTodayString();
    }
    
    const start = new Date(startDateStr);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return end.toISOString().split('T')[0];
  }

  const [startDate, setStartDate] = useState<string>(getDefaultStartDate);
  const [endDate, setEndDate] = useState<string>(getDefaultEndDate);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [visibleData, setVisibleData] = useState<any[]>([]);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);

  // Handle external prop changes (fromDate/toDate)
  useEffect(() => {
    if (fromDate && isValidDate(fromDate)) {
      setStartDate(fromDate);
    }
    if (toDate && isValidDate(toDate)) {
      setEndDate(toDate);
    }
  }, [fromDate, toDate]);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Convert YYYY-MM-DD to MM/DD/YYYY for display (with validation)
  const formatDisplayDate = (dateStr: string): string => {
    if (!dateStr || !isValidDate(dateStr)) return 'Invalid Date';
    
    const date = new Date(dateStr);
    return `${(date.getMonth() + 1)}/${date.getDate()}/${date.getFullYear()}`;
  };

  // Handle start date change
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    
    if (!newStartDate || newStartDate.trim() === '') {
      setErrorMessage("Please select a valid start date");
      return;
    }
    
    if (!isValidDate(newStartDate)) {
      setErrorMessage("Invalid start date format");
      return;
    }
    
    setStartDate(newStartDate);
    
    // Calculate new end date (max 7 days from new start date)
    const newEndDate = calculateEndDate(newStartDate);
    setEndDate(newEndDate);
    setErrorMessage(null);
  };

  // Handle end date change
  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value;
    
    if (!newEndDate || newEndDate.trim() === '') {
      setErrorMessage("Please select a valid end date");
      return;
    }
    
    if (!isValidDate(newEndDate)) {
      setErrorMessage("Invalid end date format");
      return;
    }
    
    if (!isValidDate(startDate)) {
      setErrorMessage("Please select a valid start date first");
      return;
    }
    
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(newEndDate);
    
    // Calculate difference in days
    const diffTime = endDateObj.getTime() - startDateObj.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      setErrorMessage("End date cannot be before start date");
      return;
    }
    
    if (diffDays > 6) {
      setErrorMessage("Maximum date range is 7 days");
      return;
    }
    
    setEndDate(newEndDate);
    setErrorMessage(null);
  };

  // Apply date changes and trigger API fetch
  const applyDateChanges = () => {
    if (errorMessage || !isValidDate(startDate) || !isValidDate(endDate)) return;
    
    // Call the parent callback with the new dates
    if (onDateRangeChange) {
      onDateRangeChange(startDate, endDate);
    }
    
    setIsDatePickerOpen(false);
    setCurrentPage(1);
  };

  // Reset date picker to valid defaults if needed
  const resetToDefaults = () => {
    const defaultStart = getDefaultStartDate();
    const defaultEnd = getDefaultEndDate();
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setErrorMessage(null);
  };

  // Filter data based on date range
  useEffect(() => {
    setIsLoadingData(true);

    const timer = setTimeout(() => {
      const start = fromDate ? new Date(fromDate) : null;
      const end = toDate ? new Date(toDate) : null;

      const filtered = userData.filter(user => {
        const userDate = new Date(user.date);
        if (start && end) return userDate >= start && userDate <= end;
        if (start) return userDate >= start;
        if (end) return userDate <= end;
        return true;
      });

      setFilteredData(filtered);

      const paged = showAll
        ? filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : filtered.slice(0, 5);

      setVisibleData(paged);
      setIsLoadingData(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [userData, fromDate, toDate, showAll, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow duration-300">
      <div className="flex justify-between items-center cursor-pointer hover:bg-gray-50 rounded-lg p-3 transition-colors" onClick={toggleExpand}>
        <h3 className="text-xl font-bold text-gray-800 tracking-tight">Top User Activity</h3>
        <div className="flex items-center gap-3">
          {userData.length > 5 && isExpanded && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAll(!showAll);
                setCurrentPage(1);
              }}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-700 transition-colors"
            >
              {showAll ? 'Show Less' : 'View All'}
            </button>
          )}
          <span className="text-gray-500">
            {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </span>
        </div>
      </div>

      {/* Date Filter Section */}
      {isExpanded && (
        <div className="mt-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-gray-700">Filter by Date Range</h4>
            <div className="relative">
              <button 
                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 hover:from-gray-100 hover:to-gray-200 transition-all duration-200 font-medium shadow-sm border border-gray-200"
              >
                <Calendar className="h-4 w-4 text-gray-600" />
                <span className="text-gray-700">
                  {isValidDate(startDate) && isValidDate(endDate) 
                    ? `${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`
                    : 'Select Date Range'
                  }
                </span>
              </button>
              
              {isDatePickerOpen && (
                <div className="absolute right-0 mt-2 p-4 bg-white rounded-xl shadow-xl border border-gray-200 z-10 w-80">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">From Date</label>
                      <input
                        type="date"
                        value={startDate || ''}
                        onChange={handleStartDateChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">To Date</label>
                      <input
                        type="date"
                        value={endDate || ''}
                        onChange={handleEndDateChange}
                        min={startDate}
                        max={isValidDate(startDate) ? calculateEndDate(startDate) : ''}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                      />
                      <p className="text-xs text-gray-500 mt-1 font-medium">Maximum 7 days from start date</p>
                    </div>
                    {errorMessage && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm font-medium">
                        {errorMessage}
                      </div>
                    )}
                    <div className="flex justify-between gap-2 pt-2">
                      <button 
                        onClick={() => setIsDatePickerOpen(false)}
                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={resetToDefaults}
                        className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors font-medium shadow-sm"
                      >
                        Reset
                      </button>
                      <button 
                        onClick={applyDateChanges}
                        className={`bg-indigo-600 text-white px-4 py-2 rounded-lg font-medium shadow-sm ${
                          errorMessage || !isValidDate(startDate) || !isValidDate(endDate) 
                            ? 'opacity-50 cursor-not-allowed' 
                            : 'hover:bg-indigo-700 transition-colors'
                        }`}
                        disabled={!!errorMessage || !isValidDate(startDate) || !isValidDate(endDate)}
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {isLoadingData ? (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600 font-medium">Loading user activity...</span>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserCircle className="h-8 w-8 text-gray-400" />
          </div>
          <p className="text-gray-500 text-lg font-medium">No user activity found</p>
          <p className="text-gray-400 text-sm mt-1">Try adjusting your date range or check back later</p>
        </div>
      ) : (
        isExpanded && (
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Rank</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">User</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Messages</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider">Tokens</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleData.map((user, index) => (
                    <tr key={index} className="hover:bg-white/60 transition-colors duration-150">
                      <td className="px-6 py-4 whitespace-nowrap text-left text-sm font-medium text-gray-900">{user.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          user.rank === 1 ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white shadow-lg' :
                          user.rank === 2 ? 'bg-gradient-to-r from-gray-300 to-gray-400 text-white shadow-md' :
                          user.rank === 3 ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-md' : 
                          'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800'
                        }`}>
                          {user.rank}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">{user.user_name?.charAt(0)?.toUpperCase()}</span>
                          </div>
                          <span className="text-sm font-semibold text-gray-900">{user.user_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-gray-400" />
                          <span className="text-sm font-bold text-gray-900">{user.message_count}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-left">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                          {user.token_count?.toLocaleString()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* Pagination */}
      {showAll && filteredData.length > pageSize && isExpanded && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors font-medium"
            >
              Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: totalPages }, (_, i) => {
                const page = i + 1;
                if (
                  page === 1 || page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                ) {
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                        currentPage === page 
                          ? 'bg-indigo-600 text-white shadow-sm' 
                          : 'bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 3 || page === currentPage + 3) {
                  return <span key={page} className="px-2 text-gray-400">...</span>;
                }
                return null;
              })}
            </div>

            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors font-medium"
            >
              Next
            </button>
          </div>

          <div className="flex items-center gap-2">
            <label htmlFor="pageSize" className="text-sm font-medium text-gray-700">Show:</label>
            <select
              id="pageSize"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              {[10, 20, 30, 50].map(size => (
                <option key={size} value={size}>{size} per page</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Summary Info */}
      {isExpanded && filteredData.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-100">
          <div className="flex flex-wrap justify-between text-sm text-gray-600 gap-4">
            <span>Total Users: <span className="font-semibold text-gray-800">{filteredData.length}</span></span>
            <span>Date Range: <span className="font-semibold text-gray-800">{formatDisplayDate(startDate)} - {formatDisplayDate(endDate)}</span></span>
            <span>Showing: <span className="font-semibold text-gray-800">{visibleData.length} of {filteredData.length}</span></span>
          </div>
        </div>
      )}
    </div>
  );
}